class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Add event listeners for sort dropdowns
    document.querySelectorAll('select[name="sort_by"]').forEach(select => {
      select.addEventListener('change', (event) => {
        this.onSubmitHandler(event);
      });
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Add event listener for opening mobile menu
    const mobileMenuOpenButton = document.querySelector('.mobile-facets__open');
    if (mobileMenuOpenButton) {
      mobileMenuOpenButton.addEventListener('click', this.openMobileMenu.bind(this));
    }

    // Add event listener for closing mobile menu
    const mobileMenuCloseButton = document.querySelector('.mobile-facets__close');
    if (mobileMenuCloseButton) {
      mobileMenuCloseButton.addEventListener('click', this.closeMobileMenu.bind(this));
    }

    // Add event listeners for toggling checkboxes
    this.addCheckboxToggleListeners();

    // Add event listener for mobile checkbox changes
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      mobileForm.addEventListener('change', this.onMobileFilterChange.bind(this));
    }
  }

  addCheckboxToggleListeners() {
    const facetLabels = this.querySelectorAll('.facets__label');
    facetLabels.forEach(label => {
      label.addEventListener('click', this.toggleCheckbox);
    });
  }

  toggleCheckbox(event) {
    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      // Dispatch a change event to trigger any other listeners
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  openMobileMenu() {
    const mobileMenu = document.querySelector('.mobile-facets__wrapper');
    if (mobileMenu) {
      mobileMenu.setAttribute('open', '');
      mobileMenu.querySelector('summary').setAttribute('aria-expanded', 'true');
      mobileMenu.querySelector('.mobile-facets__close').style.display = 'block';
    }
  }

  closeMobileMenu() {
    const mobileMenu = document.querySelector('.mobile-facets__wrapper');
    if (mobileMenu) {
      mobileMenu.removeAttribute('open');
      mobileMenu.querySelector('.mobile-facets__close').style.display = 'none';
      mobileMenu.querySelector('summary').setAttribute('aria-expanded', 'false');
      mobileMenu.classList.remove('menu-opening');
      document.body.classList.remove('overflow-hidden-mobile');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));

      const disclosure = mobileMenu.querySelector('details');
      if (disclosure) {
        disclosure.removeAttribute('open');
      }

      // Remove focus from the menu
      const activeElement = document.activeElement;
      if (mobileMenu.contains(activeElement)) {
        activeElement.blur();
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const isMobile = event && event.target.closest('form').id === 'FacetFiltersFormMobile';

    // Update all sort dropdowns to match the current selection
    if (searchParams.includes('sort_by=')) {
      const sortValue = new URLSearchParams(searchParams).get('sort_by');
      document.querySelectorAll('select[name="sort_by"]').forEach(select => {
        select.value = sortValue;
      });
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, isMobile)
        : FacetFiltersForm.renderSectionFromFetch(url, event, isMobile);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, isMobile) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        if (isMobile) FacetFiltersForm.renderMobileElements(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, isMobile) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    if (isMobile) FacetFiltersForm.renderMobileElements(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderMobileElements(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const mobileElements = parsedHTML.querySelectorAll('.mobile-facets__wrapper, .mobile-facets__count, .sorting');
    mobileElements.forEach((element) => {
      const container = document.querySelector(`.${element.className}`);
      if (container) {
        container.innerHTML = element.innerHTML;
      }
    });
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(html, 'text/html');
    const productGrid = parsedHtml.getElementById('ProductGridContainer');
    
    if (productGrid) {
      document.getElementById('ProductGridContainer').innerHTML = productGrid.innerHTML;

      document
        .getElementById('ProductGridContainer')
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const isTextInput = event.target.getAttribute('type') === 'text';
        const isCheckbox = event.target.getAttribute('type') === 'checkbox';

        if (!isTextInput) {
          if (isCheckbox) {
            // Restore focus to the same checkbox by matching name and value
            const checkboxName = event.target.getAttribute('name');
            const checkboxValue = event.target.getAttribute('value');
            const newCheckbox = newFacetDetailsElement.querySelector(
              `input[type="checkbox"][name="${checkboxName}"][value="${checkboxValue}"]`
            );
            if (newCheckbox) {
              newCheckbox.focus();
            }
          } else {
            // For other inputs, fall back to summary
            const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
            if (newElementToActivate) newElementToActivate.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();

    // Get the current sort value from any sort dropdown
    const sortValue = document.querySelector('select[name="sort_by"]')?.value;
    if (sortValue) {
      searchParams.append('sort_by', sortValue);
    }

    for (let [key, value] of formData.entries()) {
      // Skip sort_by as we've already handled it
      if (key === 'sort_by') continue;
      
      // Skip empty values
      if (!value) continue;
      
      const input = form.querySelector(`input[name="${key}"][value="${value}"]`);
      if (input && input.type === 'checkbox') {
        if (input.checked) {
          searchParams.append(key, value);
        }
      } else {
        searchParams.append(key, value);
      }
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    // Clear existing filter data to force a fresh fetch
    FacetFiltersForm.filterData = [];
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';
    
    // Handle sort dropdown changes
    if (event.target.name === 'sort_by') {
      const sortValue = event.target.value;
      // Update all sort dropdowns to match
      document.querySelectorAll('select[name="sort_by"]').forEach(select => {
        select.value = sortValue;
      });
    }

    if (isMobile) {
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      const searchParams = this.createSearchParams(mobileForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onMobileFilterChange(event) {
    if (event.target.type === 'checkbox') {
      this.onSubmitHandler(event);
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    // const facetLink = this.querySelector('a');
    // facetLink.setAttribute('role', 'button');
    // facetLink.addEventListener('click', this.closeFilter.bind(this));
    // facetLink.addEventListener('keyup', (event) => {
    //   event.preventDefault();
    //   if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    // });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);